import type { CatalogCard, CollectionEntry } from "../api/client";
import { validateDeckName } from "./unicodeAssignables";

// Client-side deck math, mirrored on the server. Drives Save-button
// gating and per-row errors without a round trip.

export const DECK_SIZE = 30;
export const FACTIONS = ["humans", "goblins", "dwarves", "orcs", "elves"] as const;
export type Faction = typeof FACTIONS[number];

/** Type guard — narrows an arbitrary string to a known `Faction`. */
export function isFaction(s: string | null | undefined): s is Faction {
  return !!s && (FACTIONS as readonly string[]).includes(s);
}

/** A single row in the compact right-pane list. */
export interface DeckEntry {
  card: CatalogCard;
  count: number;
}

/** Build a map of owned copy counts keyed by card id. */
export function ownershipMap(entries: CollectionEntry[]): Record<string, number> {
  const m: Record<string, number> = {};
  for (const e of entries) m[e.card_id] = e.count;
  return m;
}

/** Decks are strictly mono-faction (Neutral removed 04/2026). */
export function cardMatchesFaction(
  card: CatalogCard,
  faction: Faction,
): boolean {
  return card.faction === faction;
}

/** Copy cap for a card — 1 for legendary, otherwise 2. */
export function copyCap(card: CatalogCard): number {
  return card.rarity === "legendary" ? 1 : 2;
}

/** Drives the + button, card greying, and Inspector's Add to Deck.
 *  `reason` is the tooltip when ok is false. */
export function canAddToDeck(input: {
  card: CatalogCard;
  faction: Faction | "";
  deckCount: number;
  owned: number;
}): { ok: boolean; reason?: string } {
  const { card, faction, deckCount, owned } = input;
  if (faction && !cardMatchesFaction(card, faction)) {
    return { ok: false, reason: `Off-faction — this deck is ${faction}` };
  }
  if (owned <= 0) return { ok: false, reason: "You don't own this card" };
  const cap = copyCap(card);
  if (deckCount >= cap) {
    return {
      ok: false,
      reason: cap === 1 ? "Legendary limit (1 copy)" : "Copy cap reached (2)",
    };
  }
  if (deckCount >= owned) {
    return { ok: false, reason: `You only own ${owned}` };
  }
  return { ok: true };
}

export function defaultDeckName(faction: Faction): string {
  const pretty = faction.charAt(0).toUpperCase() + faction.slice(1);
  return `My ${pretty} Deck`;
}

/** Mono-faction → first card pins it. "" when empty. */
export function inferFaction(entries: DeckEntry[]): Faction | "" {
  for (const e of entries) {
    if (isFaction(e.card.faction)) return e.card.faction;
  }
  return "";
}

export function remainingCopies(
  card: CatalogCard,
  currentCount: number,
  owned: number,
): number {
  const headroom = copyCap(card) - currentCount;
  const unowned = owned - currentCount;
  return Math.max(0, Math.min(headroom, unowned));
}

export interface DeckStats {
  total: number;
  /** Cost → count. Costs ≥ 7 are bucketed into 7. */
  costCurve: Record<number, number>;
}

export function deckStats(entries: DeckEntry[]): DeckStats {
  const curve: Record<number, number> = {};
  let total = 0;
  for (const e of entries) {
    total += e.count;
    const bucket = e.card.cost >= 7 ? 7 : e.card.cost;
    curve[bucket] = (curve[bucket] ?? 0) + e.count;
  }
  return { total, costCurve: curve };
}

export interface ValidationIssue {
  kind:
    | "name"
    | "faction"
    | "size"
    | "off_faction"
    | "over_copy_cap"
    | "insufficient_copies";
  message: string;
}

export interface DeckValidation {
  ok: boolean;
  issues: ValidationIssue[];
}

/** Mirrors `Doi.Decks.validate_decklist/3` + name check in `Deck.changeset/2`. */
export function validateDeck(input: {
  name: string;
  faction: Faction | "";
  entries: DeckEntry[];
  owned: Record<string, number>;
}): DeckValidation {
  const issues: ValidationIssue[] = [];

  const nameResult = validateDeckName(input.name);
  if (!nameResult.ok) issues.push({ kind: "name", message: nameResult.error! });

  // Guard against malformed (non-Faction) catalog rows.
  if (input.entries.length > 0 && !input.faction) {
    issues.push({
      kind: "faction",
      message: "Could not determine deck faction from its cards",
    });
  }

  const total = input.entries.reduce((a, e) => a + e.count, 0);
  if (total !== DECK_SIZE) {
    issues.push({
      kind: "size",
      message: `Deck has ${total}/${DECK_SIZE} cards`,
    });
  }

  for (const e of input.entries) {
    if (
      input.faction &&
      !cardMatchesFaction(e.card, input.faction as Faction)
    ) {
      issues.push({
        kind: "off_faction",
        message: `${e.card.name} is not a ${input.faction} card`,
      });
    }

    const cap = copyCap(e.card);
    if (e.count > cap) {
      issues.push({
        kind: "over_copy_cap",
        message: `${e.card.name} has ${e.count} copies (cap ${cap})`,
      });
    }

    const ownedCount = input.owned[e.card.id] ?? 0;
    if (e.count > ownedCount) {
      issues.push({
        kind: "insufficient_copies",
        message: `You only own ${ownedCount} × ${e.card.name}`,
      });
    }
  }

  return { ok: issues.length === 0, issues };
}

/** Returns the same reference on rejection (over cap / un-owned). */
export function addCopy(
  entries: DeckEntry[],
  card: CatalogCard,
  owned: Record<string, number>,
): DeckEntry[] {
  const idx = entries.findIndex((e) => e.card.id === card.id);
  const current = idx >= 0 ? entries[idx].count : 0;
  const ownedCount = owned[card.id] ?? 0;

  if (current >= copyCap(card)) return entries;
  if (current >= ownedCount) return entries;

  if (idx >= 0) {
    const next = entries.slice();
    next[idx] = { ...next[idx], count: current + 1 };
    return next;
  }
  return [...entries, { card, count: 1 }];
}

export function removeCopy(
  entries: DeckEntry[],
  cardId: string,
): DeckEntry[] {
  const idx = entries.findIndex((e) => e.card.id === cardId);
  if (idx < 0) return entries;
  const next = entries.slice();
  if (next[idx].count <= 1) {
    next.splice(idx, 1);
  } else {
    next[idx] = { ...next[idx], count: next[idx].count - 1 };
  }
  return next;
}

/** Drops card_ids no longer in the catalog. */
export function entriesFromDeck(
  cards: { card_id: string; count: number }[],
  catalog: Map<string, CatalogCard>,
): DeckEntry[] {
  const out: DeckEntry[] = [];
  for (const { card_id, count } of cards) {
    const card = catalog.get(card_id);
    if (card) out.push({ card, count });
  }
  return out;
}

/** Phoenix expects `[[card_id, count], …]`. */
export function entriesToPayload(entries: DeckEntry[]): Array<[string, number]> {
  return entries.map((e) => [e.card.id, e.count]);
}

export function sortedEntries(entries: DeckEntry[]): DeckEntry[] {
  return entries.slice().sort((a, b) => {
    if (a.card.cost !== b.card.cost) return a.card.cost - b.card.cost;
    return a.card.name.localeCompare(b.card.name);
  });
}
